/**
 * A 6ª ponte — `window.__fincastAnalise` (lote de 09/10/2026, item 7).
 *
 * O QUE ELA LEVA AO SIMULADOR (script clássico, não importa módulo):
 *   · `derivarCenario` — a derivação da tabela "Demanda por unidade" sobre uma CÓPIA do
 *     empreendimento. A Comparação (renderCmp) calculava os outros cenários com o
 *     empreendimento do cenário ABERTO já derivado (`empById` cai no aberto e
 *     `aplicarTravasDemanda` escreve nele): o VGV, as unidades e as permutadas saíam os do
 *     aberto. Agora cada coluna usa a derivação DELA — a mesma função do relatório
 *     comparativo, não uma cópia;
 *   · `empDaColuna` — SOBRE QUAL cadastro cada coluna deriva. Derivar já não bastava: um irmão
 *     sem tabela (ou com tabela sem avaliação) não sobrescreve VGV/unidades/permutadas, e
 *     derivado a partir do objeto VIVO herdava os do aberto (revisão adversarial do lote). Só o
 *     aberto usa o objeto vivo, que é o que está na tela dele; o irmão usa o cadastro DELE, como
 *     o banco o devolveu (`CenarioLegado.emp` — desde a 0032 o cadastro é de cada cenário, e
 *     publicar o aberto não o muda) — o mesmo que a tela dele aberta sozinha e o relatório mostram;
 *   · `sinteseComparacao` — a síntese do fim da Comparação, que passa a ser um resumo da
 *     mesma análise do relatório (antes: "Síntese comparativa (IA)", que não era IA);
 *   · `pedirRelatorio` — o atalho "Relatório comparativo" ao lado do "Comparar operações"
 *     (EL-139) abre o diálogo da casca com os cenários marcados lá.
 *
 * Mesmo molde da ponte do motor: as funções são AS MESMAS dos módulos (identidade de
 * referência, conferida em `tests/relatorio-paridade.test.ts`), e o que depende da tela
 * (quem abre o diálogo) é configurado pela montagem.
 */
import { derivarCenario } from '../relatorio/modelo';
import { sinteseComparacao } from '../relatorio/sintese';
import { ehUuid } from '../relatorio/endereco';
import type { Empreendimento } from '../../../../engine/index';

export { derivarCenario, sinteseComparacao };

/**
 * O cadastro sobre o qual a coluna `cn` da Comparação é derivada (ver o cabeçalho).
 * `abertoId` é o id do cenário aberto (`estado.rascunho.id`); `vivo` é o empreendimento que a
 * tela tem em mãos (`empById`). Sem `emp` (o aberto, o "Otimizado" do otimizador, ou um irmão
 * montado antes desta regra), vale o vivo — que é o comportamento de antes.
 */
export function empDaColuna(
  cn: { id?: unknown; emp?: Empreendimento | null } | null | undefined,
  abertoId: unknown,
  vivo: Empreendimento,
): Empreendimento {
  if (cn === null || cn === undefined || cn.id === abertoId) return vivo;
  return cn.emp ?? vivo;
}

export interface ContextoRelatorioPonte {
  /** Abre o diálogo de escolha já com estes cenários marcados (só ids do banco). */
  abrir(preselecionados: string[]): void;
}

let ctx: ContextoRelatorioPonte | null = null;

/** Chamada pela montagem a cada cenário montado; `null` = sem relatório nesta tela. */
export function configurarRelatorio(c: ContextoRelatorioPonte | null): void {
  ctx = c;
}

/**
 * O atalho da Comparação. Recebe `estado.flxSel` como está: pode ter cenário FANTASMA (o
 * "Otimizado" do otimizador só existe na memória da tela, com id `c12`) — esses ficam de
 * fora, porque o relatório usa só o que está no banco.
 */
export function pedirRelatorio(ids: unknown): void {
  if (ctx === null) return;
  const lista = Array.isArray(ids) ? ids.filter(ehUuid) : [];
  ctx.abrir([...new Set(lista)]);
}
